import { shallowRef, ref } from "vue";
import type { Ref } from "vue";

export type CanvasScrollerOptions = {
  images?: (string | HTMLImageElement)[]; // single list used for all rows
  rowsImages?: (string | HTMLImageElement)[][]; // per-row images
  rows?: number; // number of rows
  speed?: number | number[]; // px/s per row
  gap?: number; // px between images
  paddingX?: number; // horizontal padding per side
  itemHeight?: number; // image height in px (width auto by aspect)
  rowSpacing?: number; // vertical spacing between rows
  cellWidth?: number; // 每一个单元格的宽度
  direction?: "left" | "right"; // 滚动方向
};

type InternalImageMeta = {
  img: HTMLImageElement;
  aspect: number; // naturalWidth / naturalHeight
};

export function useCanvasScroller(initialOptions?: CanvasScrollerOptions) {
  const canvasRef = shallowRef<HTMLCanvasElement | null>(null);
  const isReady: Ref<boolean> = ref(false);

  let options: Required<CanvasScrollerOptions> = {
    images: [],
    rowsImages: [],
    rows: 1,
    speed: 60,
    gap: 40,
    paddingX: 20,
    itemHeight: 52,
    rowSpacing: 0,
    cellWidth: 0,
    direction: "left",
    ...(initialOptions || {}),
  } as Required<CanvasScrollerOptions>;

  let metasRows: InternalImageMeta[][] = [];
  let animationId: number | null = null;
  let lastTs: number | null = null;
  let scrollXs: number[] = []; // per row

  const preloadImages = async (
    sources?: (string | HTMLImageElement)[],
    rowsSources?: (string | HTMLImageElement)[][]
  ) => {
    const loadList = async (list: (string | HTMLImageElement)[]) => {
      const imgs = await Promise.all(
        list.map(
          (src) =>
            new Promise<HTMLImageElement>((resolve) => {
              const img = typeof src === "string" ? new Image() : src;
              if (typeof src === "string") img.src = src;
              if (img.complete) {
                resolve(img);
              } else {
                img.onload = () => resolve(img);
                img.onerror = () => resolve(img);
              }
            })
        )
      );
      return imgs.map((img) => {
        const nw = img.naturalWidth || img.width || 1;
        const nh = img.naturalHeight || img.height || 1;
        return { img, aspect: nw / nh } as InternalImageMeta;
      });
    };

    if (rowsSources && rowsSources.length) {
      metasRows = [];
      for (const rowList of rowsSources) {
        metasRows.push(await loadList(rowList));
      }
      options.rows = rowsSources.length as number;
    } else if (sources && sources.length) {
      const metas = await loadList(sources);
      metasRows = Array.from({ length: options.rows }, () => metas);
    } else {
      metasRows = [];
    }
    isReady.value = true;
  };

  const getCanvasMetrics = () => {
    const canvas = canvasRef.value;
    if (!canvas) return null;
    const dpr = window.devicePixelRatio || 1;
    const widthCss =
      canvas.parentElement?.clientWidth ?? document.documentElement.clientWidth;
    const rowHeight = options.itemHeight;
    const totalHeightCss =
      options.rows * rowHeight + (options.rows - 1) * options.rowSpacing;
    canvas.width = Math.floor(widthCss * dpr);
    canvas.height = Math.floor(totalHeightCss * dpr);
    canvas.style.width = widthCss + "px";
    canvas.style.height = totalHeightCss + "px";
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high" as ImageSmoothingQuality;
    return { ctx, widthCss, totalHeightCss };
  };

  const computeTrackUnitWidth = (row: number) => {
    const padding = options.paddingX;
    const gap = options.gap;
    const rowMetas = metasRows[row] || [];
    let rowWidth: number;
    if (typeof options.cellWidth === "number" && options.cellWidth > 0) {
      rowWidth =
        rowMetas.length * options.cellWidth + gap * (rowMetas.length - 1) + padding * 2;
    } else {
      rowWidth =
        rowMetas.reduce((sum, m) => sum + Math.round(options.itemHeight * m.aspect), 0) +
        gap * (rowMetas.length - 1) +
        padding * 2;
    }
    return rowWidth;
  };

  const render = (
    ctx: CanvasRenderingContext2D,
    widthCss: number,
    totalHeightCss: number
  ) => {
    ctx.clearRect(0, 0, widthCss, totalHeightCss);
    const rowHeight = options.itemHeight;
    const gap = options.gap;
    const padding = options.paddingX;

    for (let row = 0; row < options.rows; row++) {
      const trackUnitWidth = computeTrackUnitWidth(row);
      const y = row * (rowHeight + options.rowSpacing); // top for this row
      const startX = -(scrollXs[row] ?? 0);
      const unitsToDraw = Math.ceil(widthCss / trackUnitWidth) + 2; // buffer
      for (let u = 0; u < unitsToDraw; u++) {
        let x = startX + u * trackUnitWidth + padding;
        const rowMetas = metasRows[row] || [];
        for (const meta of rowMetas) {
          const w = Math.round(options.itemHeight * meta.aspect);
          const h = options.itemHeight;
          const yy = y + (rowHeight - h) / 2; // center within row
          if (typeof options.cellWidth === "number" && options.cellWidth > 0) {
            const cellW = options.cellWidth;
            const dx = (cellW - w) / 2;
            const drawX = x + dx;
            if (meta.img.complete) {
              ctx.drawImage(meta.img, drawX, yy, w, h);
            }
            x += cellW + gap;
          } else {
            if (meta.img.complete) {
              ctx.drawImage(meta.img, x, yy, w, h);
            }
            x += w + gap;
          }
        }
      }
    }
  };

  const step = (ts: number) => {
    const metrics = getCanvasMetrics();
    if (!metrics) return;
    const { ctx, widthCss, totalHeightCss } = metrics;
    if (lastTs == null) lastTs = ts;
    const dt = Math.min((ts - lastTs) / 1000, 0.1); // cap delta to avoid jumps
    lastTs = ts;

    // Update scroll per row
    const speeds = Array.isArray(options.speed)
      ? options.speed
      : Array(options.rows).fill(options.speed);
    for (let r = 0; r < options.rows; r++) {
      const trackUnitWidth = computeTrackUnitWidth(r);
      const current = scrollXs[r] ?? 0;
      const baseSpeed = (speeds[r] ?? speeds[0] ?? 0) as number;
      const dirMul = options.direction === "right" ? -1 : 1;
      scrollXs[r] = current + dirMul * baseSpeed * dt;
      // wrap into [0, trackUnitWidth)
      if ((scrollXs[r] ?? 0) >= trackUnitWidth)
        scrollXs[r] = (scrollXs[r] ?? 0) - trackUnitWidth;
      else if ((scrollXs[r] ?? 0) < 0) scrollXs[r] = (scrollXs[r] ?? 0) + trackUnitWidth;
    }

    render(ctx, widthCss, totalHeightCss);

    animationId = requestAnimationFrame(step);
  };

  const handleResize = () => {
    // Recompute canvas size on resize
    const metrics = getCanvasMetrics();
    if (!metrics) return;
    render(metrics.ctx, metrics.widthCss, metrics.totalHeightCss);
  };

  const start = async () => {
    if (!canvasRef.value) return;
    await preloadImages(options.images, options.rowsImages);
    // initialize scrollXs
    scrollXs = Array(Math.max(1, options.rows)).fill(0);
    lastTs = null;
    if (animationId) cancelAnimationFrame(animationId);
    window.addEventListener("resize", handleResize);
    animationId = requestAnimationFrame(step);
  };

  const stop = () => {
    if (animationId) cancelAnimationFrame(animationId);
    animationId = null;
    window.removeEventListener("resize", handleResize);
  };

  const setOptions = async (opts: Partial<CanvasScrollerOptions>) => {
    // Merge and reinitialize where needed
    options = { ...options, ...(opts as any) };
    // Recompute scrollXs length if rows changed
    await preloadImages(options.images, options.rowsImages);
    scrollXs = Array(Math.max(1, options.rows)).fill(0);
    handleResize();
  };

  const getState = () => ({ scrollXs: [...scrollXs], ready: isReady.value });
  const getOptions = () => ({ ...options });

  return { canvasRef, start, stop, setOptions, isReady, getState, getOptions };
}
